/**
 * PublicRealtimeProvider
 *
 * Mounts the realtime connection context for the public storefront so the
 * live-conversation hooks (`useConversationEvents` → `useRealtime` →
 * `useRealtimeContext`) have a provider to read. Without this the conversation
 * thread crashed on render (`createSafeContext('Realtime')` throws when no
 * `RealtimeProvider` is mounted — the staff app mounted one, public mounted
 * nothing).
 *
 * Mirrors the staff `StaffRealtimeProvider`, EXCEPT the `tokenProvider` brokers
 * the token through the BFF (`fetchRealtimeTokens` → `/api/realtime/token`), not a
 * direct browser→monolith Connect call — so the public app stays BFF-only. The
 * WebSocket is then opened to the `websocketUrl` the token response carries.
 */
import { useNotificationEvents } from '@/shared/hooks/useNotificationEvents';
import { useRealtimeQueryBridge } from '@/shared/hooks/useRealtimeQueryBridge';
import { fetchRealtimeTokens } from '@/shared/lib/realtime';
import { RealtimeProvider, type RealtimeTokens } from '@letsgethome/realtime';
import { useCallback, type ReactNode } from 'react';

import { useAuth } from './AuthProvider';

interface Props {
  children: ReactNode;
}

export function PublicRealtimeProvider({ children }: Props) {
  // Realtime is authenticated-only: `GetToken` authorizes per-user channels and
  // 401s for anonymous visitors. The provider stays MOUNTED (the conversation
  // hooks need its context) but only CONNECTS when signed in — so an anonymous
  // home-page visit makes no wasted token request. `autoConnect` re-runs the
  // client effect on sign-in/out, so the socket comes up on login + tears down
  // on logout.
  const { isAuthenticated } = useAuth();

  const tokenProvider = useCallback(
    (channel?: string): Promise<RealtimeTokens | null> => fetchRealtimeTokens(channel),
    []
  );

  // PR-E2: the central envelope-listener bridge — every received envelope
  // (any subscribed channel) applies its declared query invalidation
  // (`realtime-query-bindings`), so e.g. the Messages tab unread badge
  // re-fetches on live conversation events. Memoised (useCallback inside)
  // so the listener registration does not churn.
  const onEnvelope = useRealtimeQueryBridge();

  return (
    <RealtimeProvider
      tokenProvider={tokenProvider}
      autoConnect={isAuthenticated}
      onEnvelope={onEnvelope}
    >
      <NotificationChannelSubscription />
      {children}
    </RealtimeProvider>
  );
}

/**
 * WP-E: subscribes the signed-in user's `notifications:{id}` channel.
 *
 * A child component rather than a hook call in the provider body, because
 * `useSubscription` reads the realtime context this very provider supplies —
 * a hook called above `RealtimeProvider` would have no context to read.
 * Renders nothing.
 */
function NotificationChannelSubscription() {
  useNotificationEvents();
  return null;
}
